import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { EventDateTimeField } from './EventDateTimeField';
import { EVENT_KIND_OPTIONS } from './eventKind';
import {
    FIELD_LABEL_CLASS,
    INPUT_CLASS,
    SUBSECTION_LABEL_CLASS,
    type SectionProps
} from './eventFormShared';

const ERROR_CLASS = 'border-red-300 focus:ring-red-500 focus:border-red-500';

function inputClass(hasError: boolean): string {
    return hasError ? `${INPUT_CLASS} ${ERROR_CLASS}` : INPUT_CLASS;
}

interface SelectFieldProps {
    value: string;
    onChange: (value: string) => void;
    children: React.ReactNode;
}

// `INPUT_CLASS` is `appearance-none`, so the select needs its own chevron.
function SelectField({ value, onChange, children }: SelectFieldProps) {
    return (
        <div className="relative">
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className={`${INPUT_CLASS} pr-8`}
            >
                {children}
            </select>
            <ChevronDownIcon className="w-4 h-4 text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
    );
}

function KindFields({ draft, setKind, isAnnouncement }: SectionProps) {
    return (
        <div>
            <label className={SUBSECTION_LABEL_CLASS}>Kind</label>
            <div className="mt-2">
                <label className={FIELD_LABEL_CLASS}>Event kind *</label>
                <SelectField value={draft.kind} onChange={setKind}>
                    {EVENT_KIND_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </SelectField>
                <p className="text-[11px] text-gray-400 mt-1">
                    {isAnnouncement
                        ? 'A notice only: no sign-up, no join link, no confirmation email.'
                        : 'Practitioners RSVP, then join over Zoom.'}
                </p>
            </div>
        </div>
    );
}

// Single-column throughout: `xl:grid-cols-2` reads the viewport, not this
// ~300px column, so a wide window would force two columns into a narrow container.
function ScheduleFields({ draft, set, errors, touched, isAnnouncement }: SectionProps) {
    return (
        <div>
            <label className={SUBSECTION_LABEL_CLASS}>Schedule</label>
            <div className="mt-2 grid grid-cols-1 gap-4">
                <EventDateTimeField
                    label="Show from"
                    value={draft.showFromLocal}
                    onChange={(v) => set('showFromLocal', v)}
                    error={touched ? errors.showFromLocal : undefined}
                    required
                />
                <EventDateTimeField
                    label={isAnnouncement ? 'Show until' : 'Starts at'}
                    value={draft.startsAtLocal}
                    onChange={(v) => set('startsAtLocal', v)}
                    error={touched ? errors.startsAtLocal : undefined}
                    required
                />
                {!isAnnouncement && (
                    <div>
                        <label className={FIELD_LABEL_CLASS}>Duration (hours) *</label>
                        <input
                            type="number"
                            min="0.25"
                            max="24"
                            step="0.25"
                            value={draft.durationHours}
                            onChange={(e) => set('durationHours', e.target.value)}
                            className={inputClass(touched && Boolean(errors.durationHours))}
                        />
                        {touched && errors.durationHours ? (
                            <p className="text-red-500 text-xs mt-1">{errors.durationHours}</p>
                        ) : (
                            <p className="text-[11px] text-gray-400 mt-1">
                                The banner comes down when the event ends.
                            </p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

/** Kind + Schedule merged - both are "what and when is this", set once early
 *  and rarely revisited afterwards, unlike Content. */
export function BasicsSection(props: SectionProps) {
    return (
        <div className="flex flex-col gap-5">
            <KindFields {...props} />
            <ScheduleFields {...props} />
        </div>
    );
}
